import { useMemo, useEffect, useRef, useState } from "react";
import Significance from "../Significance/Significance";
import UnitToggle from "../ImperialInput/UnitToggle";
import ImperialNumberField from "../ImperialInput/ImperialNumberField";
import { IMPERIAL } from "../ImperialInput/imperialUtils";
import * as translator from "../../../../utils/translator";
import "./BMISection.css";

const DEFAULT_BMI_MINIMUM_THRESHOLD = 29.9;
const DEFAULT_BMI_COMORBIDITY_THRESHOLD = 27;

const calculateBMI = (weightKg: number, heightCm: number): number | null => {
  if (!weightKg || !heightCm || weightKg <= 0 || heightCm <= 0) {
    return null;
  }
  const heightM = heightCm / 100;
  return parseFloat((weightKg / (heightM * heightM)).toFixed(1));
};

type EligibilityStatus = "eligible" | "not-eligible" | "conditional";

const getEligibilityStatus = (
  bmi: number,
  hasComorbidity: boolean,
  comorbidityThreshold: number,
  minimumThreshold: number
): EligibilityStatus => {
  if (bmi > minimumThreshold) return "eligible";
  if (bmi >= comorbidityThreshold) {
    return hasComorbidity ? "eligible" : "conditional";
  }
  return "not-eligible";
};

const extractBmiThresholds = (question: any) => {
  const rule = question?.answersGroup?.recipe?.validation_rule;
  return {
    comorbidity: rule?.bmi_comorbidity_threshold ?? DEFAULT_BMI_COMORBIDITY_THRESHOLD,
    minimum: rule?.bmi_minimum_threshold ?? DEFAULT_BMI_MINIMUM_THRESHOLD,
  };
};

/**
 * A comorbidity question is linked via bmi_comorbidity_answer_group_id; its
 * last child is a "none of the above" option, so only the preceding options
 * count as an actual illness selection.
 */
const computeHasComorbidity = (
  question: any,
  flattenedQuestionnaire: any[],
  answersSelectedMap: any
): boolean => {
  const rule = question?.answersGroup?.recipe?.validation_rule;
  const comorbidityAnswerGroupID = rule?.bmi_comorbidity_answer_group_id;
  if (!comorbidityAnswerGroupID) return false;
  const comorbidityQuestion = flattenedQuestionnaire.find(
    (q: any) => q.answersGroup?.recipe?.id === comorbidityAnswerGroupID
  );
  if (!comorbidityQuestion) return false;
  const children = comorbidityQuestion.answersGroup?.children ?? [];
  if (children.length === 0) return false;
  const illnessOptions = children.slice(0, -1);
  return illnessOptions.some((child: any) => answersSelectedMap[child.recipe.id]);
};

const getBmiStatusMessage = (
  language: string | undefined,
  bmi: number | null,
  eligibility: EligibilityStatus | null,
  hasComorbidity: boolean,
  comorbidityThreshold: number,
  minimumThreshold: number
): string | null => {
  if (bmi === null || eligibility === null) return null;

  const bmiStr = bmi.toLocaleString(language || "de", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  if (bmi > minimumThreshold) {
    return (
      translator.translate(language, "default", "QuestionnairePage/bmi_eligible_title")?.replace("{bmi}", bmiStr) ??
      null
    );
  }

  if (bmi >= comorbidityThreshold) {
    if (hasComorbidity) {
      return translator.translate(language, "default", "QuestionnairePage/bmi_conditional_pass");
    }
    return (
      translator
        .translate(language, "default", "QuestionnairePage/bmi_conditional_no_condition")
        ?.replace("{bmi}", bmiStr) ?? null
    );
  }

  return (
    translator.translate(language, "default", "QuestionnairePage/bmi_blocked")?.replace("{bmi}", bmiStr) ?? null
  );
};

const getSignificanceLevel = (eligibility: EligibilityStatus | null): "success" | "warning" | "danger" | null => {
  if (!eligibility) return null;
  if (eligibility === "eligible") return "success";
  if (eligibility === "conditional") return "warning";
  return "danger";
};

interface BMISectionProps {
  question: any;
  handleRef: any;
  onAnswerClick: any;
  formData: any;
  onTextChange: any;
  onReveal: any;
  onHide: any;
  questionnaireValidationObject: any;
  files: Array<any>;
  setFiles: any;
  dangerSignificancesRefsDispatch: (action: "add" | "remove", ref: any) => void;
  answersSelectedMap: any;
  mention: string | undefined;
  disabled?: boolean;
  language: string | undefined;
  bmiValidationFailed: boolean;
  flattenedQuestionnaire: any[];
}

const BMISection = ({
  question,
  handleRef,
  formData,
  onTextChange,
  onReveal,
  onHide,
  questionnaireValidationObject,
  dangerSignificancesRefsDispatch,
  answersSelectedMap,
  disabled,
  language,
  bmiValidationFailed,
  flattenedQuestionnaire,
}: BMISectionProps) => {
  const heightAnswersGroup = question.answersGroup;
  const weightAnswerNode = question.answersGroup?.children?.[0];

  const heightAnswerId = heightAnswersGroup?.recipe?.id;
  const weightAnswerId = weightAnswerNode?.recipe?.id;

  const heightRule = heightAnswersGroup?.recipe?.validation_rule;
  const weightRule = weightAnswerNode?.recipe?.validation_rule;

  const allowImperialHeight = !!heightRule?.allow_imperial_height;
  const allowImperialWeight = !!weightRule?.allow_imperial_weight;

  const [heightImperial, setHeightImperial] = useState(false);
  const [weightImperial, setWeightImperial] = useState(false);

  const heightValue = Number(answersSelectedMap[heightAnswerId]) || 0;
  const weightValue = Number(answersSelectedMap[weightAnswerId]) || 0;

  const ref = useRef() as React.MutableRefObject<HTMLInputElement>;

  useEffect(() => {
    handleRef(question.id, ref);
  }, []);

  useEffect(() => {
    if (heightAnswersGroup) onReveal(heightAnswersGroup);
    if (weightAnswerNode) onReveal(weightAnswerNode);

    return () => {
      if (heightAnswersGroup) onHide(heightAnswersGroup);
      if (weightAnswerNode) onHide(weightAnswerNode);
    };
  }, []);

  const hasComorbidity = useMemo(
    () => computeHasComorbidity(question, flattenedQuestionnaire, answersSelectedMap),
    [flattenedQuestionnaire, answersSelectedMap, question]
  );

  const { comorbidity: comorbidityThreshold, minimum: minimumThreshold } = useMemo(
    () => extractBmiThresholds(question),
    [question]
  );

  const bmi = useMemo(() => {
    if (heightValue > 0 && weightValue > 0) {
      return calculateBMI(weightValue, heightValue);
    }
    return null;
  }, [heightValue, weightValue]);

  const eligibility = useMemo((): EligibilityStatus | null => {
    if (bmi === null) return null;
    return getEligibilityStatus(bmi, hasComorbidity, comorbidityThreshold, minimumThreshold);
  }, [bmi, hasComorbidity, comorbidityThreshold, minimumThreshold]);

  const statusMessage = useMemo(
    () => getBmiStatusMessage(language, bmi, eligibility, hasComorbidity, comorbidityThreshold, minimumThreshold),
    [bmi, eligibility, hasComorbidity, language, comorbidityThreshold, minimumThreshold]
  );

  const significanceLevel = useMemo(() => getSignificanceLevel(eligibility), [eligibility]);

  return (
    <div className="bmi-section-container" id="bmi-section" ref={ref}>
      {/* Height input */}
      <div
        id={"question_" + question.id}
        className="questionnaire-question-item-container"
      >
        <div className="questionnaire-question-item">
          <div className="questionnaire-text-and-helpblock-container">
            <div className={`questionnaire-question-item-text-container${allowImperialHeight ? " unit-toggle-text-row" : ""}`}>
              <span dangerouslySetInnerHTML={{ __html: question.text }} />
              {allowImperialHeight && (
                <UnitToggle
                  isImperial={heightImperial}
                  onToggle={setHeightImperial}
                  metricLabel={IMPERIAL.labels.cm}
                  imperialLabel={IMPERIAL.labels.ftIn}
                />
              )}
            </div>
            {question.help_block && (
              <div
                className="questionnaire-question-item-helpblock-container"
                dangerouslySetInnerHTML={{ __html: question.help_block }}
              />
            )}
          </div>
          <div className="questionnaire-answers-container">
            <ImperialNumberField
              mode="height"
              answerId={heightAnswerId}
              validationRule={heightRule}
              questionnaireValidationObject={questionnaireValidationObject}
              onTextChange={onTextChange}
              recipeQuestionId={heightAnswersGroup?.recipe?.recipe_question_id}
              placeholder={heightAnswersGroup?.recipe?.placeholder}
              value={answersSelectedMap[heightAnswerId] || ""}
              disabled={disabled}
              isImperial={heightImperial}
            />
          </div>
        </div>
      </div>

      {/* Weight input */}
      {weightAnswerNode && (
        <div className="questionnaire-question-item-container">
          <div className="questionnaire-question-item">
            <div className="questionnaire-text-and-helpblock-container">
              <div className={`questionnaire-question-item-text-container${allowImperialWeight ? " unit-toggle-text-row" : ""}`}>
                <span>{weightAnswerNode.recipe?.label || weightAnswerNode.recipe?.text}</span>
                {allowImperialWeight && (
                  <UnitToggle
                    isImperial={weightImperial}
                    onToggle={setWeightImperial}
                    metricLabel={IMPERIAL.labels.kg}
                    imperialLabel={IMPERIAL.labels.stLb}
                  />
                )}
              </div>
            </div>
            <div className="questionnaire-answers-container">
              <ImperialNumberField
                mode="weight"
                answerId={weightAnswerId}
                validationRule={weightRule}
                questionnaireValidationObject={questionnaireValidationObject}
                onTextChange={onTextChange}
                recipeQuestionId={weightAnswerNode.recipe?.recipe_question_id}
                placeholder={weightAnswerNode.recipe?.placeholder}
                value={answersSelectedMap[weightAnswerId] || ""}
                disabled={disabled}
                isImperial={weightImperial}
              />
            </div>
          </div>
        </div>
      )}

      {/* BMI result display */}
      {bmi !== null && (
        <div className="bmi-display-container">
          <div className="bmi-result">
            <span className="bmi-result-label">
              {translator.translate(
                language,
                "default",
                "QuestionnairePage/bmi_result_label"
              )}
              :
            </span>
            <span className="bmi-result-value">{bmi.toLocaleString(language || "de", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</span>
          </div>

          {statusMessage && significanceLevel && (
            <div className="bmi-error-container">
              <Significance
                level={significanceLevel}
                text={statusMessage}
                fontWeight="500"
                fontSize="14px"
                dangerSignificancesRefsDispatch={null}
                parentID={null}
                margin="10px 0"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export {
  calculateBMI,
  extractBmiThresholds,
  getEligibilityStatus,
  computeHasComorbidity,
  getBmiStatusMessage,
  getSignificanceLevel,
  DEFAULT_BMI_MINIMUM_THRESHOLD as BMI_MINIMUM_THRESHOLD,
  DEFAULT_BMI_COMORBIDITY_THRESHOLD as BMI_COMORBIDITY_THRESHOLD,
};
export type { EligibilityStatus };
export default BMISection;
